"use client";

import { Icon } from "@plura/client/components";
import { FilterTypes, TemplateItem, templates } from "@plura/shared/lead-forms";
import { cn } from "@plura/shared/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../ui/Button";
import TemplateCard from "./TemplateCard";
import { FilterCategory, filterGroups } from "./templates";

/**
 * Landing-app template browser — a links-only, new-theme copy of the shared
 * `@plura/client/components` TemplatePicker. The interactive (preview / "use
 * template" / AI / recommended) bits are dropped; every card links to
 * `/templates/[id]`. Template data still comes from `@plura/shared/lead-forms`.
 */
const TemplatePicker = () => {
  const [selectedCategory, setSelectedCategory] = useState<FilterCategory | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedDescription =
    selectedCategory?.description ??
    "Browse ready-made templates to spark ideas and jump-start your next form or quiz.";

  const filterTemplates = (category: FilterTypes) => {
    setSelectedCategory(filterGroups.flatMap((group) => group.options).find((option) => option.value === category) ?? null);
  };

  // Debounce search term
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => setDebouncedSearchTerm(searchTerm), 250);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm]);

  // Relevance score for search matching
  const calculateRelevance = (template: TemplateItem, search: string): number => {
    if (!search) return 0;

    const searchLower = search.toLowerCase();
    const titleLower = template.title.toLowerCase();
    const descriptionLower = template.description.toLowerCase();

    let score = 0;

    if (titleLower === searchLower) score += 100;
    else if (titleLower.startsWith(searchLower)) score += 80;
    else if (titleLower.includes(searchLower)) score += 60;

    const searchWords = searchLower.split(/\s+/).filter((word) => word.length > 2);
    searchWords.forEach((word) => {
      if (titleLower.includes(word)) score += 30;
      if (descriptionLower.includes(word)) score += 10;
    });

    if (searchWords.length > 1) {
      const regex = new RegExp(searchWords.join(".*"), "i");
      if (regex.test(titleLower)) score += 40;
      if (regex.test(descriptionLower)) score += 20;
    }

    return score;
  };

  const filteredTemplates = useMemo(() => {
    let filtered = templates;

    if (selectedCategory) {
      filtered = filtered.filter((template) => template.tags.includes(selectedCategory.value));
    }

    if (debouncedSearchTerm) {
      filtered = filtered
        .map((template) => ({ template, score: calculateRelevance(template, debouncedSearchTerm) }))
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score)
        .map(({ template }) => template);
    }

    return filtered;
  }, [selectedCategory, debouncedSearchTerm]);

  const heading = selectedCategory
    ? selectedCategory.isTemplate
      ? `${selectedCategory.label} Templates`
      : selectedCategory.label
    : "All Templates";

  return (
    <div className="landing-container flex flex-row gap-12 max-lg:flex-col max-lg:gap-8">
      {/* Sidebar — category filters */}
      <aside className="w-[230px] shrink-0 max-lg:w-full">
        <div className="sticky top-[104px] flex flex-col gap-7 max-lg:static">
          {filterGroups.map((group) => (
            <div key={group.title} className="flex flex-col gap-1">
              <h5 className="mb-2 px-4 font-body text-[11px] font-bold uppercase tracking-[0.12em] text-dark/45">
                {group.title}
              </h5>
              {group.options.map((option) => {
                const active = selectedCategory?.value === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => filterTemplates(option.value)}
                    className={cn(
                      "flex h-[42px] w-full items-center rounded-full px-4 text-left font-body text-[15px] transition-colors duration-150",
                      active
                        ? "bg-primary/10 font-semibold text-primary"
                        : "font-medium text-[#555555] hover:bg-shade hover:text-dark",
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </aside>

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        {selectedCategory && (
          <div className="flex items-center gap-1.5 font-body text-sm font-medium text-[#555555]">
            <button onClick={() => setSelectedCategory(null)} className="transition-colors hover:text-dark">
              All Templates
            </button>
            <span className="text-dark/40">/</span>
            <span className="text-dark">{selectedCategory.label}</span>
          </div>
        )}

        <h1 className="text-h3 font-heading text-dark">{heading}</h1>
        <p className="text-description max-w-[760px]">{selectedDescription}</p>

        {/* Search */}
        <div className="relative mt-4 max-w-[520px]">
          <Icon
            name="search"
            className="pointer-events-none absolute left-4 top-1/2 z-[1] !size-5 -translate-y-1/2 text-[#999999] [&_path]:fill-current"
            aria-hidden
          />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search all templates and categories"
            autoComplete="off"
            aria-label="Search templates"
            className="h-12 w-full rounded-full border border-[#e5e5e5] bg-white pl-11 pr-5 font-body text-[15px] text-dark outline-none transition-colors placeholder:text-[#999999] focus:border-green"
          />
        </div>

        {/* Grid */}
        {filteredTemplates.length > 0 ? (
          <div className="mt-8 grid grid-cols-3 gap-x-8 gap-y-12 max-lg:grid-cols-2 max-md:grid-cols-1">
            {filteredTemplates.map((template) => (
              <TemplateCard key={template.id} template={template} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center gap-5 rounded-3xl border border-[#e5e5e5] bg-shade px-5 py-16 text-center">
            <h3 className="font-heading text-2xl font-medium text-dark">Didn&apos;t find what you&apos;re looking for?</h3>
            <p className="font-body text-[#555555]">Try our AI form builder and get exactly what you need.</p>
            <Button href="https://app.formsuite.co/sign-up">Try with AI</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplatePicker;
